import type {ReactNode} from 'react'
import {faSearch} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import sortBy from 'lodash/sortBy'
import sumBy from 'lodash/sumBy'
import {numberToHumanFormat} from '@freckle/educator-entities/ts/common/helpers/string-helper'
import {Label} from '@freckle/educator-materials/ts/components/label'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {Panel} from '@freckle/educator-materials/ts/components/panel'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {
  link,
  sourceWrapper,
  summaryPanel,
  bodyWrapper,
  totalWrapper,
  total,
  sourceItem,
  rosteredByGroup,
  panelStyle,
} from './summary-panel.module.scss'

type Props = {
  title: string
  linkUrl: string
  counts: Array<CountT>
  scrollToTabs?: () => void
  sortFirstType: CountTypeT | undefined | null
}

export type CountTypeT = 'admin' | 'teacher' | 'classlink' | 'clever' | 'renaissance' | 'idpsync'

type CountT = {
  type: CountTypeT
  count: number
  linkUrl?: string
  popOver?: ReactNode
}

export function SummaryPanel(props: Props) {
  const {title, linkUrl, counts, scrollToTabs, sortFirstType} = props
  const {t} = useSafeTranslation()

  const linkNode = (
    <Link href={linkUrl} reactRouter className={link} onClick={scrollToTabs}>
      <FontAwesomeIcon icon={faSearch} />
    </Link>
  )

  const sortedCounts = sortBy(counts, ({type, count}) =>
    type === sortFirstType ? Number.NEGATIVE_INFINITY : -count,
  )

  const filteredCounts = sortedCounts.filter(({count}) => count > 0)

  const sourceItems = filteredCounts.map(({type, count, linkUrl, popOver}, i: number) => (
    <PanelSubItem
      key={i}
      title={t('MANAGE_TEACHERS_SUMMARY_PANEL_SUB_ITEM_COUNT_TYPE_TITLE', {type})}
      value={count}
      linkUrl={linkUrl}
      scrollToTabs={scrollToTabs}
      popOver={popOver}
    />
  ))

  const sourceNode =
    sourceItems.length > 0 ? (
      <div className={sourceWrapper}>
        <Label>{t('MANAGE_TEACHERS_SUMMARY_PANEL_ROSTERED_BY_LABEL')}</Label>
        {sourceItems}
      </div>
    ) : null

  const totalCount = sumBy(counts, ({count}) => count)

  return (
    <div className={summaryPanel}>
      <Panel headingTitle={title} headingAction={linkNode} className={panelStyle}>
        <div className={bodyWrapper}>
          <div className={totalWrapper}>
            <Label>{t('MANAGE_TEACHERS_SUMMARY_PANEL_TOTAL_LABEL')}</Label>
            <span className={total}>{numberToHumanFormat(totalCount)}</span>
          </div>
          {sourceNode}
        </div>
      </Panel>
    </div>
  )
}

type PanelSubItemProps = {
  title: string
  value: number
  linkUrl: string | undefined | null
  scrollToTabs?: (() => void) | null
  popOver?: ReactNode
}

function PanelSubItem(props: PanelSubItemProps) {
  const {title, value, linkUrl, scrollToTabs, popOver} = props
  const valStr = numberToHumanFormat(value)
  const valueNode =
    linkUrl !== null && linkUrl !== undefined ? (
      <Link href={linkUrl} reactRouter onClick={scrollToTabs}>
        {valStr}
      </Link>
    ) : (
      <span>{valStr}</span>
    )

  return (
    <div className={sourceItem}>
      <div className={rosteredByGroup}>
        <span>{title}</span>
        {popOver}
      </div>
      {valueNode}
    </div>
  )
}
